import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { shows } from '@/mocks/shows';
import { filterShows, paginate, searchShows, sortShows, type SortKey } from '@/utils/shows';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import ShowCard from '@/components/feature/ShowCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterPanel, { type RangeOption } from '@/components/feature/FilterPanel';
import EmptyState from '@/components/feature/EmptyState';
import SectionHeading from '@/components/feature/SectionHeading';

const PAGE_SIZE = 12;

const scoreOptions: RangeOption[] = [
  { label: '不限' },
  { label: '9.0 分以上', min: 9 },
  { label: '8.5 分以上', min: 8.5 },
  { label: '8.0 分以上', min: 8 },
];

const yearOptions: RangeOption[] = [
  { label: '不限' },
  { label: '2024-2026', min: 2024, max: 2026 },
  { label: '2020-2023', min: 2020, max: 2023 },
  { label: '2018-2019', min: 2018, max: 2019 },
];

const sortOptions: { label: string; key: SortKey }[] = [
  { label: '按评分', key: 'score' },
  { label: '按更新时间', key: 'updated' },
  { label: '按年份', key: 'year' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [input, setInput] = useState(query);

  const [scoreIndex, setScoreIndex] = useState(0);
  const [yearIndex, setYearIndex] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useDocumentMeta({
    title: query ? `搜索“${query}” - 高评分电视剧` : '搜索剧集 - 高评分电视剧',
    description: query
      ? `在“${query}”的搜索结果中浏览匹配的高评分电视剧，支持评分、年份与标签筛选。`
      : '输入关键词，搜索匹配的高评分电视剧，支持按评分与年份筛选。',
  });

  const matched = useMemo(() => (query ? searchShows(shows, query) : []), [query]);

  const availableTags = useMemo(() => {
    const set = new Set<string>();
    matched.forEach((show) => show.tags.forEach((item) => set.add(item)));
    return Array.from(set);
  }, [matched]);

  const filtered = useMemo(() => {
    if (!query) return [];
    const options = {
      minScore: scoreOptions[scoreIndex].min,
      maxScore: scoreOptions[scoreIndex].max,
      minYear: yearOptions[yearIndex].min,
      maxYear: yearOptions[yearIndex].max,
      tag,
    };
    return sortShows(filterShows(matched, options), sortKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched, query, scoreIndex, yearIndex, sortKey, tag]);

  const visible = paginate(filtered, page, PAGE_SIZE);
  const hasMore = filtered.length > visible.length;

  const recommended = useMemo(
    () => sortShows(shows, 'score').slice(0, 10),
    [],
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = input.trim();
    setPage(1);
    setTag('');
    setScoreIndex(0);
    setYearIndex(0);
    setSearchParams(trimmed ? { q: trimmed } : {});
  };

  const resetFilters = () => {
    setScoreIndex(0);
    setYearIndex(0);
    setSortKey('score');
    setTag('');
    setPage(1);
  };

  return (
    <div className="px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: '搜索结果' }]} />

      <header className="mt-5 md:mt-7 max-w-3xl">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">搜索高评分电视剧</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600">
          支持按剧名、简介、类型标签或主演搜索，快速定位你想看的剧集。
        </p>
      </header>

      <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
        <div className="flex items-center gap-2 flex-1 h-12 px-4 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors">
          <i className="ri-search-line text-foreground-400 text-lg"></i>
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="输入剧名、类型或主演关键词"
            className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            aria-label="搜索关键词"
          />
        </div>
        <button
          type="submit"
          className="h-12 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      {query && (
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          <span className="text-foreground-600">
            搜索关键词：<strong className="text-primary-600">{query}</strong>
          </span>
          <span className="w-1 h-1 rounded-full bg-background-400"></span>
          <span className="text-foreground-500">
            匹配 <strong className="text-accent-600">{matched.length}</strong> 部剧集，当前筛选后 {filtered.length} 部
          </span>
        </div>
      )}

      {query && matched.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setFilterOpen((prev) => !prev)}
            className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i>
            筛选与排序
          </button>
          <div className={`${filterOpen ? 'block' : 'hidden'} lg:block mt-3 lg:mt-0`}>
            <FilterPanel
              scoreOptions={scoreOptions}
              yearOptions={yearOptions}
              sortOptions={sortOptions}
              activeScore={scoreIndex}
              activeYear={yearIndex}
              activeSort={sortKey}
              availableTags={availableTags}
              activeTag={tag}
              onScoreChange={(index) => {
                setScoreIndex(index);
                setPage(1);
              }}
              onYearChange={(index) => {
                setYearIndex(index);
                setPage(1);
              }}
              onSortChange={(key) => {
                setSortKey(key);
                setPage(1);
              }}
              onTagChange={(value) => {
                setTag(value);
                setPage(1);
              }}
              onReset={resetFilters}
            />
          </div>
        </div>
      )}

      {!query ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-search-line"
            title="请输入关键词开始搜索"
            description="例如：古装、悬疑、星际、京都等，都可以作为搜索关键词。"
          />
          <section className="mt-12">
            <SectionHeading title="热门高评分推荐" subtitle="不知道看什么？先从这些开始" icon="ri-fire-line" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
              {recommended.map((show) => (
                <ShowCard key={show.id} show={show} />
              ))}
            </div>
          </section>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-emotion-sad-line"
            title="暂无搜索结果"
            description={`没有找到与“${query}”匹配的剧集，试试更换关键词，或放宽筛选条件。`}
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
          <section className="mt-12">
            <SectionHeading title="换个方向看看" subtitle="为你推荐热门高评分剧集" icon="ri-magic-line" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
              {recommended.slice(0, 10).map((show) => (
                <ShowCard key={show.id} show={show} />
              ))}
            </div>
          </section>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5 mt-8">
            {visible.map((show) => (
              <ShowCard key={show.id} show={show} showIntro />
            ))}
          </div>
          {hasMore ? (
            <div className="flex justify-center mt-10">
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-down-line"></i>
                加载更多
              </button>
            </div>
          ) : (
            <p className="text-center text-sm text-foreground-400 mt-10">已显示全部 {filtered.length} 部匹配剧集</p>
          )}
        </>
      )}
    </div>
  );
}